import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams, HttpResponse } from '@angular/common/http';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { GarageListResult, GaragePort } from './garage.port';
import {
  Mezzo,
  CreateMezzoDto,
  UpdateMezzoDto,
  AddManutenzioneDto,
  MezzoQueryParams,
  PaginatedResponse,
  TipologiaMezzo,
  MezzoManutenzione,
  StoricoManutenzioniQueryParams,
  MezzoAssociazione,
  StoricoAssociazioniQueryParams, AssociateToDipendenteDto, StatoMezzo,
} from '../../models/garage.model';
import { environment } from '../../../environments/environment';
import {TenantService} from '../tenant.service';

@Injectable({ providedIn: 'root' })
export class GarageHttpService implements GaragePort {
  private http = inject(HttpClient);
  private tenantService = inject(TenantService);
  private base = `${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/mezzi`;
  private listCache = new Map<string, { etag: string; response: PaginatedResponse<Mezzo> }>();

  getList(params?: MezzoQueryParams): Observable<GarageListResult> {
    let httpParams = new HttpParams();
    if (params?.wordSearch) httpParams = httpParams.set('wordSearch', params.wordSearch);
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.size != null) httpParams = httpParams.set('size', String(params.size));
    if (params?.sort) params.sort.forEach(s => { httpParams = httpParams.append('sort', s); });

    const cacheKey = httpParams.toString();
    const cached = this.listCache.get(cacheKey);
    let headers = new HttpHeaders();

    if (cached?.etag) {
      headers = headers.set('If-None-Match', cached.etag);
    }

    return this.http.get<PaginatedResponse<Mezzo>>(this.base, {
      params: httpParams,
      headers,
      observe: 'response',
    }).pipe(
      map((response: HttpResponse<PaginatedResponse<Mezzo>>) => {
        const body = response.body ?? {
          content: [],
          totalElements: 0,
          totalPages: 0,
          pageIndex: params?.page ?? 0,
          pageSize: params?.size ?? 20,
        };
        const etag = response.headers.get('ETag') ?? response.headers.get('Etag');

        if (etag) {
          this.listCache.set(cacheKey, { etag, response: body });
        }

        return { notModified: false, response: body } as GarageListResult;
      }),
      catchError((err) => {
        if (err?.status === 304) {
          return of({ notModified: true } as GarageListResult);
        }
        throw err;
      })
    );
  }

  getById(id: number): Observable<Mezzo> {
    return this.http.get<Mezzo>(`${this.base}/${id}`);
  }

  create(dto: CreateMezzoDto, scheda?: File | null): Observable<number> {
    const formData = new FormData();

    formData.append(
      'data',
      new Blob([JSON.stringify(dto)], { type: 'application/json' })
    );

    if (scheda) {
      formData.append('scheda', scheda);
    }

    return this.http.post<number>(this.base, formData);
  }

  update(id: number, dto: UpdateMezzoDto): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}`, dto);
  }

  associateToCantiere(id: number, idCantiere: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/associateToCantiere/${idCantiere}`, {});
  }

  endCantiereAssociation(id: number): Observable<number> {
    return this.http.patch<number>(`${this.base}/${id}/endCantiereAssociation`, {});
  }

  addManutenzione(dto: AddManutenzioneDto): Observable<number> {
    return this.http.patch<number>(`${this.base}/addManutenzione`, dto);
  }

  getStoricoManutenzioni(params?: StoricoManutenzioniQueryParams): Observable<PaginatedResponse<MezzoManutenzione>> {
    let httpParams = new HttpParams();
    if (params?.idMezzo != null) httpParams = httpParams.set('idMezzo', String(params.idMezzo));
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.size != null) httpParams = httpParams.set('size', String(params.size));
    if (params?.sort) params.sort.forEach(s => { httpParams = httpParams.append('sort', s); });
    return this.http.get<PaginatedResponse<MezzoManutenzione>>(`${this.base}/storicoManutenzioni`, { params: httpParams });
  }

  getStoricoAssociazioni(params?: StoricoAssociazioniQueryParams): Observable<PaginatedResponse<MezzoAssociazione>> {
    let httpParams = new HttpParams();
    if (params?.idMezzo != null) httpParams = httpParams.set('idMezzo', String(params.idMezzo));
    if (params?.page != null) httpParams = httpParams.set('page', String(params.page));
    if (params?.size != null) httpParams = httpParams.set('size', String(params.size));
    if (params?.sort) params.sort.forEach(s => { httpParams = httpParams.append('sort', s); });
    return this.http.get<PaginatedResponse<MezzoAssociazione>>(`${this.base}/storicoAssociazioni`, { params: httpParams });
  }

  associateToDipendente(dto: AssociateToDipendenteDto): Observable<number> {
    return this.http.patch<number>(
      `${this.base}/associateToDipendente`,
      dto
    );
  }

  endDipendenteAssociation(
    id: number,
    kmRitorno: number,
    dataRitorno: string
  ): Observable<number> {
    const params = new HttpParams()
      .set('kmRitorno', String(kmRitorno))
      .set('dataRitorno', dataRitorno);

    return this.http.patch<number>(
      `${this.base}/${id}/endDipendenteAssociation`,
      null,
      { params }
    );
  }

  setUsability(id: number, stato: StatoMezzo): Observable<number> {
    const params = new HttpParams().set('stato', stato);

    return this.http.patch<number>(
      `${this.base}/${id}/setUsability`,
      null,
      { params }
    );
  }

  getTipologieMezzi(): Observable<PaginatedResponse<TipologiaMezzo>> {
    const params = new HttpParams().set('size', '200');
    return this.http.get<PaginatedResponse<TipologiaMezzo>>(`${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/tipologieMezzi`, { params });
  }

  createTipologiaMezzo(tipo: string): Observable<number> {
    return this.http.post<number>(`${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/tipologieMezzi`, { tipo });
  }

  deleteTipologiaMezzo(id: number): Observable<number> {
    return this.http.delete<number>(`${environment.apiBaseUrl || ''}/${this.tenantService.tenant}/tipologieMezzi/${id}`);
  }
}
